import { registerMockAPI } from '~/mocks/mockApis';

import { useAPI, useAsyncHandler } from '~/hooks/common';

import { API_ENDPOINT } from '~/constants';

import type { TypeExchangeData, TypeGetExchangeOrdersParams, TypeGetExchangeOrdersResponse } from '~/types/claim.types';
import type { TypeErrorResponseData } from '~/types/common.types';

/**
 * [교환 신청/조회] 교환 신청 리스트 조회 api 요청 훅
 */
const useGetExchangeOrders = () => {
  const { runAsync } = useAsyncHandler();

  const getExchangeOrders = useAPI<TypeGetExchangeOrdersParams, TypeGetExchangeOrdersResponse>({
    endpoint: API_ENDPOINT.CLAIM.GET_EXCHANGE_ORDERS,
    method: 'get',
  });

  const requestExchangeOrders = async (
    params: TypeGetExchangeOrdersParams,
    {
      onSuccess,
      onError,
    }: {
      onSuccess?: (data: TypeGetExchangeOrdersResponse) => void;
      onError?: (error: TypeErrorResponseData['error']) => void;
    }
  ) => {
    runAsync(() => getExchangeOrders({ params }), {
      onSuccess: (data) => onSuccess?.(data),
      onError: (error: TypeErrorResponseData['error']) => onError?.(error),
    });
  };

  return { requestExchangeOrders };
};

export default useGetExchangeOrders;

const EXCHANGE_LIST_ROWS: [status: TypeExchangeData['status'], exchangeType: TypeExchangeData['exchangeType']][] = [
  [
    { code: 'RETURN_RECEIPT_COMPLETE', name: '회수예정' },
    { code: 'NORMAL', name: '일반교환' },
  ],
  [
    { code: 'RETURN_DELIVERY_ING', name: '회수중' },
    { code: 'PRE_EXCHANGE', name: '선교환' },
  ],
  [
    { code: 'RETURN_INSPECTION_SUCCESS', name: '검수성공' },
    { code: 'NORMAL', name: '일반교환' },
  ],
  [
    { code: 'RETURN_INSPECTION_FAILED', name: '검수실패' },
    { code: 'NORMAL', name: '일반교환' },
  ],
  [
    { code: 'RETURN_DELIVERY_CANCELED', name: '회수취소' },
    { code: 'PRE_EXCHANGE', name: '선교환' },
  ],
];
const EXCHANGE_MEMBER_NAMES = ['김컬리', '이물류', '박배송', '최반품', '정교환'];

const makeExchangeOrder = (index: number): TypeExchangeData => {
  const [status, exchangeType] = EXCHANGE_LIST_ROWS[index - 1] ?? EXCHANGE_LIST_ROWS[0];
  return {
    exchangeReceiptId: `EX20260519000${index}`,
    clientOrderCode: `CL-20260518-000${index}`,
    outboundOrderCode: `OB20260518000${index}`,
    returnCourier: { code: 'LOTTE', name: '롯데' },
    status,
    member: { name: EXCHANGE_MEMBER_NAMES[index - 1] ?? '김컬리' },
    returnOrderItems: [{ goodsCode: `G0000000${index}`, goodsName: `[컬리] 교환 대상 상품 ${index}` }],
    returnOrderTrackings: index === 1 ? [] : [{ invoiceNumber: `25009876${index}00` }],
    exchangeOutboundOrderCode: index === 1 ? '' : `OB20260519100${index}`,
    exchangeType,
    exchangeOrderItems:
      index === 3
        ? [
            { goodsCode: 'G00000003', goodsName: '[컬리] 교환 대상 상품 3' },
            { goodsCode: 'G00000021', goodsName: '[컬리] 대체 배송 상품' },
          ]
        : [{ goodsCode: `G0000000${index}`, goodsName: `[컬리] 교환 대상 상품 ${index}` }],
    exchangeReceiptAt: `2026-05-19 08:2${index}:00`,
    updatedAt: `2026-05-19 08:5${index}:00`,
  };
};

const buildExchangeOrdersResponse = (count: number): TypeGetExchangeOrdersResponse => ({
  data: Array.from({ length: count }, (_, i) => makeExchangeOrder(i + 1)),
  pagination: { listView: 50, listTotal: count, cursor: 0, totalCount: count },
});

registerMockAPI<TypeGetExchangeOrdersResponse>({
  method: 'get',
  endpoint: '/api-oms/exchange-orders',
  label: '반품교환/교환신청조회/목록',
  scenarios: [
    { name: '정상 응답 — 5건', response: buildExchangeOrdersResponse(5) },
    { name: '검색 결과 0건', response: buildExchangeOrdersResponse(0) },
    {
      name: '서버 에러 (500)',
      response: { error: { code: 500, message: '교환 목록을 불러오지 못했습니다.' } },
      status: 500,
    },
  ],
});
